import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useRealtime } from "@/lib/realtime";
import { AlertTriangle, UserX, Clock, CheckCircle2, ShieldAlert, History, Filter, RefreshCw, Compass, X as XIcon, User, Loader2, Timer } from "lucide-react";
import { toast } from "sonner";

const TYPE_ICON = { absence: UserX, delay: Clock, incident: AlertTriangle, route_deviation: Compass };
const SEV_COLOR = { low: "bg-zinc-200", medium: "bg-[#FFC60A]", high: "bg-red-500 text-white" };
const STATUS_COLOR = {
  ON_TIME: "bg-emerald-500 text-white",
  LATE_1_MIN: "bg-yellow-400 text-zinc-900",
  LATE_3_MIN: "bg-orange-500 text-white",
  CRITICAL: "bg-red-500 text-white",
};

const VALID_TABS = new Set(["incidents", "delays", "deviations"]);

export default function AdminIncidents() {
  const [params] = useSearchParams();
  const initialTab = VALID_TABS.has(params.get("tab")) ? params.get("tab") : "incidents";
  const initialStatus = params.get("status") || "";
  const [tab, setTab] = useState(initialTab);
  const [incidents, setIncidents] = useState([]);
  const [delays, setDelays] = useState([]);
  const [delayStatus, setDelayStatus] = useState(initialStatus);
  const [deviations, setDeviations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [resolving, setResolving] = useState(null); // currently-resolving incident object

  const loadIncidents = useCallback(async () => {
    try { const { data } = await api.get("/incidents"); setIncidents(data); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
  }, []);

  const loadDelays = useCallback(async () => {
    setLoading(true);
    try {
      const q = delayStatus ? `?status=${delayStatus}&limit=100` : "?limit=100";
      const { data } = await api.get(`/logibus/live/delay-events${q}`);
      setDelays(data || []);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  }, [delayStatus]);

  const loadDeviations = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/logibus/route-deviations?off_route_only=true&limit=100");
      setDeviations(data || []);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadIncidents(); }, [loadIncidents]);
  useEffect(() => { if (tab === "delays") loadDelays(); }, [tab, loadDelays]);
  useEffect(() => { if (tab === "deviations") loadDeviations(); }, [tab, loadDeviations]);

  // Realtime: incidents + alerts + assignment mutations (single source).
  // A new assignment may resolve a "no driver" alert; a moved tour may
  // change which bus is concerned by a delay — so we always reload.
  const wsRefresh = useCallback(() => {
    loadIncidents();
    if (tab === "delays") loadDelays();
    if (tab === "deviations") loadDeviations();
  }, [tab, loadIncidents, loadDelays, loadDeviations]);
  useRealtime(["alerts", "tour_assignments"], {
    "incident.created":   wsRefresh,
    "incident.resolved":  wsRefresh,
    "assignment.created":        wsRefresh,
    "assignment.updated":        wsRefresh,
    "assignment.deleted":        wsRefresh,
    "assignment.moved":          wsRefresh,
    "assignment.driver_changed": wsRefresh,
    "assignment.upserted":       wsRefresh,
  });

  const resolve = async (id, payload) => {
    try {
      await api.post(`/incidents/${id}/resolve`, payload || {});
      toast.success("Incident résolu — notifications & escalades arrêtées");
      setResolving(null);
      loadIncidents();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    }
  };

  const fmtDuration = (mins) => {
    if (mins === null || mins === undefined) return null;
    if (mins < 1) return "<1 min";
    if (mins < 60) return `${mins} min`;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m ? `${h}h ${m}min` : `${h}h`;
  };

  const deviationsCount = incidents.filter((i) => i.type === "route_deviation" && i.status !== "resolved").length;

  return (
    <div className="p-6 lg:p-10" data-testid="admin-incidents-page">
      <header className="mb-6">
        <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500">Sécurité opérationnelle</div>
        <h1 className="font-display font-black uppercase text-4xl lg:text-5xl tracking-tighter">Retards & incidents</h1>
        <p className="text-sm text-zinc-600 mt-2">Suivi unifié des retards détectés et incidents signalés par les chauffeurs.</p>
      </header>

      <div className="inline-flex border border-zinc-900 mb-5" data-testid="tabs">
        <button onClick={() => setTab("incidents")} data-testid="tab-incidents"
          className={`px-4 py-2 text-xs font-bold uppercase tracking-widest inline-flex items-center gap-2 ${tab === "incidents" ? "bg-zinc-900 text-[#FFC60A]" : "bg-white hover:bg-zinc-100"}`}>
          <ShieldAlert size={14} /> Incidents <span className="font-mono text-[10px] opacity-70">{incidents.length}</span>
        </button>
        <button onClick={() => setTab("delays")} data-testid="tab-delays"
          className={`px-4 py-2 text-xs font-bold uppercase tracking-widest inline-flex items-center gap-2 border-l border-zinc-900 ${tab === "delays" ? "bg-zinc-900 text-[#FFC60A]" : "bg-white hover:bg-zinc-100"}`}>
          <History size={14} /> Retards <span className="font-mono text-[10px] opacity-70">{delays.length}</span>
        </button>
        <button onClick={() => setTab("deviations")} data-testid="tab-deviations"
          className={`px-4 py-2 text-xs font-bold uppercase tracking-widest inline-flex items-center gap-2 border-l border-zinc-900 ${tab === "deviations" ? "bg-zinc-900 text-[#FFC60A]" : "bg-white hover:bg-zinc-100"}`}>
          <Compass size={14} /> Hors-itinéraire <span className="font-mono text-[10px] opacity-70">{deviationsCount}</span>
        </button>
      </div>

      {tab === "incidents" && (
        <div className="border border-zinc-900 max-w-5xl divide-y divide-zinc-200" data-testid="incidents-list">
          {incidents.length === 0 ? (
            <div className="p-8 text-sm text-zinc-500 flex flex-col items-center gap-2">
              <ShieldAlert size={28} /> Aucun incident signalé.
            </div>
          ) : incidents.map((i) => {
            const Icon = TYPE_ICON[i.type] || AlertTriangle;
            const checks = i.resolution_checks || {};
            const checkLabels = [
              checks.driver_contacted && "Chauffeur contacté",
              checks.passengers_informed && "Passagers informés",
              checks.incident_settled && "Réglé",
              checks.false_positive && "Faux positif",
            ].filter(Boolean);
            return (
              <div key={i.id} className={`p-4 flex items-start gap-3 ${i.status === "resolved" ? "bg-zinc-50" : ""}`} data-testid={`incident-row-${i.id}`}>
                <div className={`w-9 h-9 border border-zinc-900 flex items-center justify-center ${i.status === "resolved" ? "bg-emerald-50" : "bg-white"}`}>
                  {i.status === "resolved" ? <CheckCircle2 size={16} className="text-emerald-700" /> : <Icon size={16} />}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold uppercase text-xs tracking-widest">{i.type}</span>
                    {i.severity && (
                      <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest border border-zinc-900 ${SEV_COLOR[i.severity] || "bg-white"}`}>
                        {i.severity === "low" ? "Faible" : i.severity === "medium" ? "Moyen" : "Élevé"}
                      </span>
                    )}
                    {i.delay_minutes > 0 && <span className="text-xs font-mono text-zinc-500">+{i.delay_minutes} min</span>}
                    {i.status === "resolved" && (
                      <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest border border-zinc-900 bg-emerald-500 text-white" data-testid="resolved-badge">
                        Résolu
                      </span>
                    )}
                    <span className="text-[10px] font-mono text-zinc-500 ml-auto">{i.created_at?.slice(0, 16).replace("T", " ")}</span>
                  </div>
                  <div className="text-sm mt-1">{i.message}</div>
                  <div className="text-[10px] font-mono text-zinc-500 mt-1 uppercase tracking-widest">par {i.reporter_name} · {i.reporter_role}</div>

                  {i.status === "resolved" && (
                    <div className="mt-2 border-l-2 border-emerald-500 pl-3 py-1 text-[11px] space-y-0.5 bg-white" data-testid={`resolution-block-${i.id}`}>
                      <div className="flex items-center gap-3 text-zinc-700">
                        <span className="inline-flex items-center gap-1"><User size={11} className="text-zinc-500" /><strong>{i.resolved_by_user_name || "—"}</strong></span>
                        <span className="font-mono text-zinc-500">{(i.resolved_at || "").slice(0, 16).replace("T", " ")}</span>
                        {i.resolution_time_minutes !== null && i.resolution_time_minutes !== undefined && (
                          <span className="inline-flex items-center gap-1 font-mono text-zinc-600"><Timer size={11} /> Résolu en {fmtDuration(i.resolution_time_minutes)}</span>
                        )}
                      </div>
                      {i.resolution_action && (
                        <div><span className="font-mono uppercase tracking-widest text-[9px] text-zinc-500 mr-1">Action :</span>{i.resolution_action}</div>
                      )}
                      {i.resolution_comment && (
                        <div><span className="font-mono uppercase tracking-widest text-[9px] text-zinc-500 mr-1">Commentaire :</span>{i.resolution_comment}</div>
                      )}
                      {checkLabels.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {checkLabels.map((l) => (
                            <span key={l} className="px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-widest bg-emerald-100 text-emerald-800 border border-emerald-300">✓ {l}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {i.status !== "resolved" ? (
                  <button data-testid={`resolve-incident-${i.id}`} onClick={() => setResolving(i)} className="text-xs font-bold uppercase tracking-widest border border-zinc-900 px-3 py-2 hover:bg-zinc-900 hover:text-white inline-flex items-center gap-1">
                    <CheckCircle2 size={12} /> Résoudre
                  </button>
                ) : (
                  <span className="text-[10px] font-mono uppercase tracking-widest text-emerald-700">Clôturé</span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {resolving && (
        <ResolveIncidentModal
          incident={resolving}
          onClose={() => setResolving(null)}
          onConfirm={(payload) => resolve(resolving.id, payload)}
        />
      )}

      {tab === "delays" && (
        <div className="max-w-5xl" data-testid="delays-list">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <Filter size={12} className="text-zinc-500" />
            {["", "ON_TIME", "LATE_1_MIN", "LATE_3_MIN", "CRITICAL"].map((s) => (
              <button key={s || "all"} onClick={() => setDelayStatus(s)} data-testid={`delay-filter-${s || "all"}`}
                className={`px-2 py-1 text-[10px] font-bold uppercase tracking-widest border border-zinc-900 ${delayStatus === s ? "bg-[#FFC60A]" : "bg-white hover:bg-zinc-100"}`}>
                {s === "" ? "Tous" : s.replace("_", " ")}
              </button>
            ))}
            <button onClick={loadDelays} className="ml-auto px-2 py-1 text-[10px] font-bold uppercase tracking-widest border border-zinc-900 inline-flex items-center gap-1">
              {loading ? <span className="animate-pulse">…</span> : <RefreshCw size={11} />} Rafraîchir
            </button>
          </div>
          <div className="border border-zinc-900 overflow-x-auto">
            {delays.length === 0 ? (
              <div className="p-8 text-sm text-zinc-500 text-center">Aucun retard enregistré.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 border-b border-zinc-900">
                  <tr>
                    <th className="text-left px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-zinc-500">Date</th>
                    <th className="text-left px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-zinc-500">Ligne</th>
                    <th className="text-left px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-zinc-500">Arrêt</th>
                    <th className="text-left px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-zinc-500">Prévu</th>
                    <th className="text-left px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-zinc-500">Retard</th>
                    <th className="text-left px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-zinc-500">Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {delays.map((d) => (
                    <tr key={d.id} className="border-b border-zinc-200 hover:bg-zinc-50">
                      <td className="px-3 py-2 font-mono text-xs">{new Date(d.created_at).toLocaleString("fr-FR")}</td>
                      <td className="px-3 py-2 font-display font-bold">{d.route_short_name || "—"}</td>
                      <td className="px-3 py-2 truncate max-w-[200px]">{d.stop_name}</td>
                      <td className="px-3 py-2 font-mono text-xs">{String(d.scheduled_time || "").slice(0, 5)}</td>
                      <td className="px-3 py-2 font-mono text-xs">+{d.delay_minutes} min</td>
                      <td className="px-3 py-2">
                        <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest border border-zinc-900 ${STATUS_COLOR[d.status] || "bg-zinc-200"}`}>
                          {d.status?.replace("_", " ")}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
      {tab === "deviations" && (
        <div className="max-w-5xl" data-testid="deviations-list">
          <div className="flex items-center gap-2 mb-3 flex-wrap text-xs text-zinc-600">
            <Compass size={12} className="text-zinc-500" />
            Bus actuellement <strong>hors couloir de sécurité</strong> (vitesse &gt; 3 km/h, écart au tracé OSRM).
            <button onClick={loadDeviations} className="ml-auto px-2 py-1 text-[10px] font-bold uppercase tracking-widest border border-zinc-900 inline-flex items-center gap-1">
              {loading ? <span className="animate-pulse">…</span> : <RefreshCw size={11} />} Rafraîchir
            </button>
          </div>
          <div className="border border-zinc-900 overflow-x-auto">
            {deviations.length === 0 ? (
              <div className="p-8 text-sm text-zinc-500 text-center flex flex-col items-center gap-2">
                <Compass size={28} /> Tous les bus sont sur leur itinéraire.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 border-b border-zinc-900">
                  <tr>
                    <th className="text-left px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-zinc-500">Détecté</th>
                    <th className="text-left px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-zinc-500">Bus</th>
                    <th className="text-left px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-zinc-500">Trip</th>
                    <th className="text-left px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-zinc-500">Position</th>
                    <th className="text-left px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-zinc-500">Écart</th>
                    <th className="text-left px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-zinc-500">Couloir</th>
                  </tr>
                </thead>
                <tbody>
                  {deviations.map((d) => (
                    <tr key={d.id} className="border-b border-zinc-200 hover:bg-zinc-50" data-testid={`deviation-row-${d.id}`}>
                      <td className="px-3 py-2 font-mono text-xs">{new Date(d.created_at).toLocaleString("fr-FR")}</td>
                      <td className="px-3 py-2 font-display font-bold text-xs">{d.vehicle_id}</td>
                      <td className="px-3 py-2 font-mono text-xs">{(d.trip_id || "").slice(0, 16)}</td>
                      <td className="px-3 py-2 font-mono text-[10px]">{d.lat?.toFixed(4)}, {d.lon?.toFixed(4)}</td>
                      <td className="px-3 py-2 font-mono text-xs">
                        <span className="px-2 py-0.5 bg-red-500 text-white font-bold">{Math.round(d.distance_m)} m</span>
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-zinc-500">{d.buffer_m} m</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ResolveIncidentModal({ incident, onClose, onConfirm }) {
  const [comment, setComment] = useState("");
  const [action, setAction] = useState("");
  const [driverContacted, setDriverContacted] = useState(false);
  const [passengersInformed, setPassengersInformed] = useState(false);
  const [incidentSettled, setIncidentSettled] = useState(true);
  const [falsePositive, setFalsePositive] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    try {
      await onConfirm({
        comment,
        action_taken: action,
        driver_contacted: driverContacted,
        passengers_informed: passengersInformed,
        incident_settled: incidentSettled,
        false_positive: falsePositive,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="resolve-incident-modal">
      <div className="bg-white border border-zinc-900 w-full max-w-lg">
        <div className="border-b border-zinc-900 p-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500">Clôture incident</div>
            <div className="font-display font-black text-xl tracking-tighter">Résoudre incident</div>
          </div>
          <button onClick={onClose} data-testid="resolve-close" className="text-zinc-500 hover:text-zinc-900"><XIcon size={20} /></button>
        </div>

        <div className="p-4 space-y-4">
          <div className="text-xs text-zinc-600 bg-zinc-50 border border-zinc-200 p-2">
            <strong>{incident.type?.toUpperCase()}</strong> — {incident.message}
          </div>

          <div>
            <label className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500">Action effectuée</label>
            <input data-testid="resolve-action" value={action} onChange={(e) => setAction(e.target.value)}
              placeholder="Ex : Chauffeur rappelé, redéploiement bus de secours…"
              className="mt-1 w-full border border-zinc-900 px-3 py-2 text-sm font-mono" />
          </div>

          <div>
            <label className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500">Commentaire (optionnel)</label>
            <textarea data-testid="resolve-comment" value={comment} onChange={(e) => setComment(e.target.value)}
              placeholder="Détails complémentaires pour l'historique…"
              rows={3} className="mt-1 w-full border border-zinc-900 px-3 py-2 text-sm font-mono" />
          </div>

          <div className="border border-zinc-900 p-3 space-y-2">
            <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500 mb-1">Vérifications</div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" data-testid="check-driver-contacted" checked={driverContacted}
                onChange={(e) => setDriverContacted(e.target.checked)} />
              Chauffeur contacté
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" data-testid="check-passengers-informed" checked={passengersInformed}
                onChange={(e) => setPassengersInformed(e.target.checked)} />
              Passagers informés
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" data-testid="check-incident-settled" checked={incidentSettled}
                onChange={(e) => setIncidentSettled(e.target.checked)} />
              Incident réglé
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" data-testid="check-false-positive" checked={falsePositive}
                onChange={(e) => setFalsePositive(e.target.checked)} />
              Faux positif (incident non avéré)
            </label>
          </div>

          <div className="text-[10px] font-mono text-zinc-500 bg-amber-50 border border-amber-200 p-2">
            ⓘ Confirmer la résolution arrête les notifications répétées, alertes IA et escalades. L'incident reste visible dans l'historique et les analytics.
          </div>
        </div>

        <div className="border-t border-zinc-900 p-4 flex items-center justify-end gap-2">
          <button onClick={onClose} data-testid="resolve-cancel"
            className="px-4 py-2 border border-zinc-900 font-bold text-xs uppercase tracking-widest hover:bg-zinc-100">
            Annuler
          </button>
          <button onClick={submit} disabled={submitting} data-testid="resolve-confirm"
            className="px-4 py-2 bg-emerald-600 text-white border border-zinc-900 font-bold text-xs uppercase tracking-widest hover:bg-emerald-700 inline-flex items-center gap-1 disabled:opacity-60">
            {submitting ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
            Confirmer résolution
          </button>
        </div>
      </div>
    </div>
  );
}
